<template>
  <!-- Figma row 4: full = hero + rail icon | minimal = rail-only centered (set VITE_CONNECTING_LAYOUT=minimal) -->
  <v-container
    fluid
    class="pa-0 fill-height bg-backgroundBase vc-incoming-root vc-tv-canvas-bg"
  >
    <PatientBackground />

    <template v-if="!minimalLayout">
      <div class="vc-incoming-hero">
        <h1 class="vc-incoming-hero__title text-wrap">{{ headline }}</h1>
        <p class="vc-incoming-hero__purpose text-wrap">{{ purposeLine }}</p>
        <div class="vc-incoming-hero__connecting">
          <span class="vc-incoming-hero__connecting-text">Connecting you now...</span>
          <v-progress-circular color="primary" indeterminate size="48" width="4" />
        </div>
      </div>

      <v-sheet
        class="vc-incoming-panel vc-connecting-panel d-flex flex-column bg-white"
        elevation="24"
        height="100%"
        rounded="0"
      >
        <div class="mb-16">
          <v-icon class="vc-rail__video-icon" color="primary">mdi-video-plus-outline</v-icon>
        </div>
        <div class="flex-grow-1" aria-hidden="true" />
      </v-sheet>
    </template>

    <v-sheet
      v-else
      class="vc-incoming-panel vc-connecting-panel vc-connecting-rail--minimal d-flex flex-column bg-white"
      elevation="24"
      height="100%"
      rounded="0"
    >
      <v-icon class="vc-rail__video-icon mb-4" color="primary">mdi-video-plus-outline</v-icon>
      <p class="vc-minimal-connecting-label mb-6">Connecting you now...</p>
      <v-progress-circular color="primary" indeterminate size="48" width="4" />
    </v-sheet>
  </v-container>
</template>

<script setup lang="ts">
/**
 * Brief flash before ActiveCallView; matches Figma connecting variants.
 */
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useCallStore } from '../stores/callStore'
import PatientBackground from '../components/PatientBackground.vue'
import { incomingPurposeLine } from '@virtual-care/shared'

interface Props {
  sessionId: string
}

const props = defineProps<Props>()
const router = useRouter()
const callStore = useCallStore()

const minimalLayout = process.env.VUE_APP_CONNECTING_LAYOUT === 'minimal'

const nurseName = computed(() => callStore.nurseName ?? 'your nurse')
const headline = computed(() => `Connecting to ${nurseName.value}...`)
const purposeLine = computed(() => incomingPurposeLine(callStore.callPurpose))

onMounted(async () => {
  // Small delay to let the previous router transition (incoming → connecting) complete
  // before navigating again. Without this, Vue Router can mount/unmount ActiveCallView
  // multiple times in rapid succession, causing useVideoClient to initialize and dispose
  // repeatedly, which closes the Zoom session prematurely.
  await new Promise(resolve => setTimeout(resolve, 100))
  router.replace(`/call/${props.sessionId}`)
})
</script>
